/**
 * InstructorCard Component
 *
 * Displays a detailed card for an instructor in the directory (full-width list view)
 */

import { h } from 'preact';
import { route } from 'preact-router';
import type { InstructorCard as InstructorCardData } from '../../services/instructorService';

interface InstructorCardProps {
  instructor: InstructorCardData;
  className?: string;
}

export function InstructorCard({ instructor, className = '' }: InstructorCardProps) {
  // Navigate to instructor detail page
  const handleClick = () => {
    route(`/directorio/${instructor.id}`);
  };

  // Handle keyboard navigation (Enter key)
  const handleKeyPress = (e: h.JSX.TargetedKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      handleClick();
    }
  };

  // Build location string
  const locationParts = [
    instructor.location.city,
    instructor.location.state,
    instructor.location.country
  ].filter(Boolean);
  const locationString = locationParts.join(', ') || 'Ubicación no especificada';

  // Get active materials (with defensive checks) - immutable approach
  const materials = [
    { condition: instructor.materials?.has_crystals_bed, label: 'Cama de cristales' },
    { condition: instructor.materials?.has_arrow, label: 'Flechas' },
    { condition: instructor.materials?.has_steel_bars, label: 'Barras de acero' },
    { condition: instructor.materials?.has_concrete_blocks, label: 'Bloques hormigón' },
    { condition: instructor.materials?.has_other && instructor.materials?.other_description?.trim(), label: instructor.materials?.other_description || 'Otro material' }
  ].filter(item => item.condition).map(item => item.label);

  // Get active providers (with defensive checks) - immutable approach
  const providers = [
    { condition: instructor.providers?.provides_wood, label: 'Leña' },
    { condition: instructor.providers?.provides_tepe, label: 'Tepe' },
    { condition: instructor.providers?.provides_iron_bar, label: 'Barra de hierro' },
    { condition: instructor.providers?.provides_arrows, label: 'Flechas' },
    { condition: instructor.providers?.provides_wood_board, label: 'Tabla de madera' },
    { condition: instructor.providers?.provides_concrete_block, label: 'Bloque de hormigón' }
  ].filter(item => item.condition).map(item => item.label);

  return (
    <div
      onClick={handleClick}
      onKeyPress={handleKeyPress}
      role="button"
      tabIndex={0}
      class={`bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden ${className}`}
      aria-label={`Ver perfil de ${instructor.name}`}
    >
      {/* Content Section */}
      <div class="p-6 flex flex-col">
          {/* Header: Name and Location */}
          <div class="mb-4">
            <div class="flex items-center gap-3 mb-2">
              <h3 class="text-2xl font-bold text-gray-900">
                {instructor.name}
              </h3>
              {/* Verified Badge */}
              {instructor.verified && (
                <div class="bg-green-500 text-white px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1">
                  <svg
                    class="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  Verificado
                </div>
              )}
            </div>

            {/* Location */}
            <div class="flex items-center gap-2 text-gray-600 mb-2">
              <svg
                class="w-5 h-5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span class="text-sm">{locationString}</span>
            </div>

            {/* Fire Experience and Certification Year */}
            <div class="flex flex-wrap items-center gap-3">
              {instructor.nivel_de_experiencia_en_fuego && (
                <div class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                  <svg
                    class="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  {instructor.nivel_de_experiencia_en_fuego}
                </div>
              )}

              {instructor.certification?.year && (
                <div class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 14l9-5-9-5-9 5 9 5z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
                    />
                  </svg>
                  Certificado en {instructor.certification?.year}
                </div>
              )}

              {instructor.is_professional && (
                <div class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  Profesional de Desarrollo Personal
                </div>
              )}
            </div>
          </div>

          {/* Specialties Section - now showing as badges */}
          {instructor.specialties && Array.isArray(instructor.specialties) && instructor.specialties.length > 0 && (
            <div class="mb-4 pb-4 border-b border-gray-200">
              <p class="text-xs font-semibold text-gray-500 uppercase mb-2">Especialidades</p>
              <div class="flex flex-wrap gap-2">
                {instructor.specialties.map((specialty) => (
                  <span
                    key={specialty}
                    class="inline-block px-2.5 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded text-xs"
                  >
                    {specialty}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Materials and Providers Badges */}
          <div class="space-y-3 mb-4">
            {/* Materials */}
            {materials.length > 0 && (
              <div>
                <p class="text-xs font-semibold text-gray-500 uppercase mb-2">Material Disponible</p>
                <div class="flex flex-wrap gap-2">
                  {materials.map((material) => (
                    <span
                      key={material}
                      class="inline-block px-2.5 py-1 bg-green-50 text-green-700 border border-green-200 rounded text-xs"
                    >
                      {material}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Providers */}
            {providers.length > 0 && (
              <div>
                <p class="text-xs font-semibold text-gray-500 uppercase mb-2">Servicios que Proporciona</p>
                <div class="flex flex-wrap gap-2">
                  {providers.map((provider) => (
                    <span
                      key={provider}
                      class="inline-block px-2.5 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded text-xs"
                    >
                      {provider}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer: View Profile Button */}
          <div class="mt-auto pt-4 border-t border-gray-200 flex items-center justify-end">
            <button
              type="button"
              class="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
              onClick={handleClick}
            >
              Ver perfil completo
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
      </div>
    </div>
  );
}

/**
 * InstructorCardSkeleton Component
 *
 * Loading skeleton for InstructorCard
 */
export function InstructorCardSkeleton({ className = '' }: { className?: string }) {
  return (
    <div class={`bg-white rounded-lg shadow-md overflow-hidden animate-pulse ${className}`}>
      {/* Content Skeleton */}
      <div class="p-6">
          {/* Name Skeleton */}
          <div class="h-8 bg-gray-300 rounded mb-3 w-2/3" />

          {/* Location Skeleton */}
          <div class="h-4 bg-gray-200 rounded mb-3 w-1/2" />

          {/* Badges Skeleton */}
          <div class="flex gap-2 mb-4">
            <div class="h-7 bg-gray-200 rounded-full w-24" />
            <div class="h-7 bg-gray-200 rounded-full w-32" />
          </div>

          {/* Specialties Skeleton */}
          <div class="space-y-2 mb-4 pb-4 border-b border-gray-200">
            <div class="h-3 bg-gray-200 rounded w-full" />
            <div class="h-3 bg-gray-200 rounded w-5/6" />
            <div class="h-3 bg-gray-200 rounded w-4/6" />
          </div>

          {/* Materials Skeleton */}
          <div class="mb-3">
            <div class="h-3 bg-gray-200 rounded mb-2 w-24" />
            <div class="flex gap-2">
              <div class="h-6 bg-gray-200 rounded w-20" />
              <div class="h-6 bg-gray-200 rounded w-16" />
              <div class="h-6 bg-gray-200 rounded w-24" />
            </div>
          </div>

          {/* Providers Skeleton */}
          <div class="mb-4">
            <div class="h-3 bg-gray-200 rounded mb-2 w-32" />
            <div class="flex gap-2">
              <div class="h-6 bg-gray-200 rounded w-16" />
              <div class="h-6 bg-gray-200 rounded w-20" />
            </div>
          </div>

          {/* Footer Skeleton */}
          <div class="pt-4 border-t border-gray-200 flex items-center justify-between">
            <div class="h-4 bg-gray-200 rounded w-32" />
            <div class="h-10 bg-gray-300 rounded-lg w-40" />
          </div>
      </div>
    </div>
  );
}
